/**
 * Custom hook untuk mengambil restriction rate berdasarkan tanggal dan rate ID.
 *
 * API Endpoint: GET `/api/restriction`
 * API Version: 4.0
 *
 * @param {string} gte - Tanggal checkin
 * @param {string} lte - Tanggal checkout
 * @param {string} rateId - ID rate
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { data, isLoading, error } = useRestrictionRate({ gte: '2022-01-01', lte: '2022-01-31', rateId: 'rate-id' });
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { RestrictionRateSchema } from "../response/RestrictionRateResponse";

const API_VERSION = 'v4';

interface RestrictionRateProps {
    gte: string;
    lte: string;
    rateId?: string;
}

const useRestrictionRate = ({ gte, lte, rateId }: RestrictionRateProps) => {
    return useQuery({
        queryKey: ['bookings', 'monthly', 'calendar', 'restriction', gte, lte, rateId],
        queryFn: async () => {
            try {
                const response = await privateApi.get(`/${API_VERSION}/restriction`, {
                    params: {
                        filter: {
                            ...(rateId && { rate_plan_id: rateId }),
                            date: {
                                gte,
                                lte,
                            }
                        },
                    }
                });

                // Validasi response
                const validationResult = RestrictionRateSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Restriction data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Restriction`, error);
                throw error;
            }
        },
        enabled: !!gte && !!lte,
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false
    });
};

export default useRestrictionRate;